import { useEffect, useState } from 'react';
import { useObjectPendencies } from '../../../hooks/useObjectPendencies';
import {
  PendencyDirection,
  collectPendencyTargets,
  findFirstOffscreenPendency,
} from '../detailsPendencyFab.utils';

const PENDENCY_KEY_SEPARATOR = '|';

const OBSERVER_THRESHOLD_STEPS = 20;

const OBSERVER_THRESHOLDS = Array.from(
  { length: OBSERVER_THRESHOLD_STEPS + 1 },
  (_, step) => step / OBSERVER_THRESHOLD_STEPS,
);

interface UsePendencyFabResult {
  isVisible: boolean;
  direction: PendencyDirection;
  scrollToNextPendency: () => void;
}

export const usePendencyFab = (scrollNode: HTMLElement | null): UsePendencyFabResult => {
  const { pendencies, isRevealed } = useObjectPendencies();
  const [targetNode, setTargetNode] = useState<HTMLElement | null>(null);
  const [direction, setDirection] = useState<PendencyDirection>('down');

  const pendencyKeySignature = pendencies
    .map((pendency) => pendency.key)
    .join(PENDENCY_KEY_SEPARATOR);

  useEffect(() => {
    if (!scrollNode || !isRevealed || !pendencyKeySignature) {
      setTargetNode(null);

      return;
    }

    const pendencyKeys = new Set(pendencyKeySignature.split(PENDENCY_KEY_SEPARATOR));
    let observedTargets: HTMLElement[] = [];

    const syncTarget = () => {
      const placements = observedTargets.map((target) => ({
        target,
        bounds: target.getBoundingClientRect(),
      }));
      const offscreen = findFirstOffscreenPendency(placements, scrollNode.getBoundingClientRect());

      setTargetNode(offscreen?.target ?? null);
      setDirection(offscreen?.direction ?? 'down');
    };

    const intersectionObserver = new IntersectionObserver(syncTarget, {
      root: scrollNode,
      threshold: OBSERVER_THRESHOLDS,
    });

    const observePendencyTargets = () => {
      intersectionObserver.disconnect();
      observedTargets = collectPendencyTargets(scrollNode, pendencyKeys);
      observedTargets.forEach((target) => intersectionObserver.observe(target));
      syncTarget();
    };

    let scheduledFrame = 0;
    const mutationObserver = new MutationObserver(() => {
      cancelAnimationFrame(scheduledFrame);
      scheduledFrame = requestAnimationFrame(observePendencyTargets);
    });

    observePendencyTargets();
    mutationObserver.observe(scrollNode, {
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      cancelAnimationFrame(scheduledFrame);
      mutationObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [scrollNode, isRevealed, pendencyKeySignature]);

  return {
    isVisible: isRevealed && targetNode !== null,
    direction,
    scrollToNextPendency: () => {
      targetNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  };
};
